import * as React from 'react';
import { ERouteControlViewType, IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import { renderToStaticMarkup } from 'react-dom/server';
import { useSelector } from 'react-redux';

import { MapBalloon } from '../../../components/Map/parts';
import { getIsDistanceCalculatingEnabled } from '../../../selectors/distanceCalculating';
import { getIsMapServicesVisible } from '../../../selectors/mapServices';
import {
  IRegularInfrastructureData,
  IRecommendationNewbuildingInfrastructure,
  ITAInfrastructureData,
  TTAInfrastructureType,
  TInfrastructureItem,
  EMapService,
} from '../../../types/map';
import { findGeoObjectDatum, isTransportAccessibilityInfrastructureItem } from '../helpers';
import { trackTransportAccessibilityPinClick } from '../analytics';

interface IUseGeoObjectBalloonParams {
  regularInfrastructureByYearRef: React.MutableRefObject<Map<number, IRegularInfrastructureData>>;
  transportAccessibilityInfrastructureByTypeRef: React.MutableRefObject<
    Map<TTAInfrastructureType, ITAInfrastructureData>
  >;
  onRouteButtonClick(item: TInfrastructureItem, routeControlViewType: ERouteControlViewType): void;
  onSimilarNewbuildingClick(item: IRecommendationNewbuildingInfrastructure): void;
  resetTARouting: () => void;
  mapService: EMapService | null;
}

const ROUTE_BUTTON_PREFIX = 'route-button-';

export const useGeoObjectBalloon = ({
  regularInfrastructureByYearRef,
  transportAccessibilityInfrastructureByTypeRef,
  onRouteButtonClick,
  onSimilarNewbuildingClick,
  resetTARouting,
  mapService,
}: IUseGeoObjectBalloonParams) => {
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);
  const isMapServicesVisible = useSelector(getIsMapServicesVisible);

  const handleBalloonHtmlFirstFetch = React.useCallback(
    async (geoObject: IMapGeoObject): Promise<string> => {
      const {
        options: { id },
      } = geoObject;

      const datum = findGeoObjectDatum(
        id,
        regularInfrastructureByYearRef.current,
        transportAccessibilityInfrastructureByTypeRef.current,
        mapService,
      );
      const item = datum?.infrastructureItem;

      if (!item) {
        return Promise.reject(new Error('Не верный тип пина!'));
      }

      const markup = renderToStaticMarkup(
        <MapBalloon
          infrastructureItem={item}
          isDistanceCalculatingEnabled={isDistanceCalculatingEnabled}
          routeButtonIdPrefix={ROUTE_BUTTON_PREFIX}
        />,
      );

      return Promise.resolve(markup);
    },
    [isDistanceCalculatingEnabled, mapService],
  );

  const handleBalloonOpen = React.useCallback(
    (geoObject: IMapGeoObject) => {
      const datum = findGeoObjectDatum(
        geoObject.options.id,
        regularInfrastructureByYearRef.current,
        transportAccessibilityInfrastructureByTypeRef.current,
        mapService,
      );
      const item = datum?.infrastructureItem as TInfrastructureItem;

      const routeButton = document.getElementById(ROUTE_BUTTON_PREFIX + item.id);

      if (routeButton) {
        const routeControlViewType = isMapServicesVisible
          ? ERouteControlViewType.stickyLeft
          : ERouteControlViewType.default;

        routeButton.addEventListener('click', () => {
          onRouteButtonClick(item, routeControlViewType);
        });
      }

      if (item.type === 'similar_newbuilding') {
        const links = document.querySelectorAll(`#${item.id} a`);

        links.forEach(link =>
          link.addEventListener('click', () => {
            onSimilarNewbuildingClick(item);
          }),
        );
      }

      if (isTransportAccessibilityInfrastructureItem(item)) {
        trackTransportAccessibilityPinClick(item.type as TTAInfrastructureType);
        resetTARouting();
      }
    },
    [isMapServicesVisible, onRouteButtonClick, onSimilarNewbuildingClick, resetTARouting, mapService],
  );

  return { handleBalloonHtmlFirstFetch, handleBalloonOpen };
};
